import { Check, Languages } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@aitoken/ui/components/dropdown-menu';
import { cn } from '@aitoken/ui/lib/utils';
import { LOCALE_LABELS, isLocale, type Locale } from '@aitoken/contracts';
import { useAvailableLocales, useLocale, setLocale } from './index';
import { useCommonT } from './messages/common';

/** 选择语言后的副作用（例如已登录时同步到账号，用于邮件语言）。由应用注册。 */
let afterChange: ((l: Locale) => void) | null = null;
export function onUserLocaleChange(fn: (l: Locale) => void) {
  afterChange = fn;
}

function choose(v: string) {
  if (!isLocale(v)) return;
  void setLocale(v).then(() => afterChange?.(v));
}

/** 独立的语言按钮（官网顶栏、登录页）：地球图标 + 当前语言名，窄屏只显示图标；只有一种语言的站点（国内站）不显示 */
export function LanguageSwitcher({ className, compact = false }: { className?: string; compact?: boolean }) {
  const locale = useLocale();
  const locales = useAvailableLocales();
  const t = useCommonT();
  if (locales.length < 2) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          className,
        )}
        aria-label={t('language.switch')}
      >
        <Languages className="size-4" aria-hidden />
        <span className={cn(compact && 'sr-only')}>{LOCALE_LABELS[locale]}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        {locales.map((l) => (
          <DropdownMenuItem key={l} onSelect={() => choose(l)} lang={l} className="justify-between gap-6">
            {LOCALE_LABELS[l]}
            {l === locale ? <Check className="size-4" aria-hidden /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** 嵌在账号菜单里的二级菜单 */
export function LanguageSubMenu() {
  const locale = useLocale();
  const locales = useAvailableLocales();
  const t = useCommonT();
  if (locales.length < 2) return null;
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2">
        <Languages className="size-4" aria-hidden />
        <span className="flex-1">{t('language')}</span>
        <span className="text-muted-foreground">{LOCALE_LABELS[locale]}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-40">
        <DropdownMenuRadioGroup value={locale} onValueChange={choose}>
          {locales.map((l) => (
            <DropdownMenuRadioItem key={l} value={l} lang={l}>
              {LOCALE_LABELS[l]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
